import * as React from "react"
import { addDays, format } from "date-fns"
import { CalendarIcon } from "lucide-react"
import { DateRange } from "react-day-picker"

import { Calendar } from "./Calendar"
import { Popover, PopoverContent, PopoverTrigger } from "./Popover"
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./Select"

interface DateRangePickerProps {
  value?: DateRange
  onChange?: (range: DateRange | undefined) => void
  className?: string
}

const presets = [
  {
    label: "Last 7 days",
    value: "7d",
    range: () => ({
      from: addDays(new Date(), -7),
      to: new Date(),
    }),
  },
  {
    label: "Last 30 days", 
    value: "30d",
    range: () => ({
      from: addDays(new Date(), -30),
      to: new Date(),
    }),
  },
  {
    label: "Last 90 days",
    value: "90d", 
    range: () => ({
      from: addDays(new Date(), -90),
      to: new Date(),
    }),
  },
  {
    label: "Last 180 days",
    value: "180d",
    range: () => ({
      from: addDays(new Date(), -180),
      to: new Date(),
    }),
  },
  {
    label: "Last year",
    value: "1y",
    range: () => ({
      from: addDays(new Date(), -365),
      to: new Date(),
    }),
  },
  {
    label: "Custom range",
    value: "custom",
    range: () => undefined,
  },
]

export function DateRangePickerNew({
  value,
  onChange,
  className,
}: DateRangePickerProps) {
  const [date, setDate] = React.useState<DateRange | undefined>(value)
  const [selectedPreset, setSelectedPreset] = React.useState<string>(() => {
    // Try to match the current date range to a preset
    if (!value?.from || !value?.to) return "30d"
    
    const daysDiff = Math.round((value.to.getTime() - value.from.getTime()) / (1000 * 60 * 60 * 24))
    
    if (daysDiff <= 7) return "7d"
    if (daysDiff <= 30) return "30d"
    if (daysDiff <= 90) return "90d"
    if (daysDiff <= 180) return "180d"
    if (daysDiff <= 365) return "1y"
    
    return "custom"
  })

  React.useEffect(() => {
    setDate(value)
  }, [value])

  const handlePresetChange = (presetValue: string) => {
    setSelectedPreset(presetValue)
    const preset = presets.find(p => p.value === presetValue)
    if (preset && preset.range) {
      const range = preset.range()
      setDate(range)
      onChange?.(range)
    }
  }

  const handleCalendarSelect = (range: DateRange | undefined) => {
    setDate(range)
    onChange?.(range)
    if (range?.from && range?.to) {
      setSelectedPreset("custom")
    }
  }

  const formatDateRange = (dateRange: DateRange | undefined) => {
    if (!dateRange?.from) {
      return "Select date range"
    }
    
    if (dateRange.to) {
      return `${format(dateRange.from, "dd/MM/yyyy")} - ${format(dateRange.to, "dd/MM/yyyy")}`
    }
    
    return format(dateRange.from, "dd/MM/yyyy")
  }

  return (
    <div className={`flex gap-2 ${className}`}>
      <Select value={selectedPreset} onValueChange={handlePresetChange}>
        <SelectTrigger className="w-[180px]">
          <SelectValue />
        </SelectTrigger>
        <SelectContent>
          {presets.map((preset) => (
            <SelectItem key={preset.value} value={preset.value}>
              {preset.label}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>

      {selectedPreset === "custom" && (
        <Popover>
          <PopoverTrigger asChild>
            <button
              className={`
                inline-flex items-center justify-start text-left font-normal h-10 px-3 py-2 
                bg-white border border-gray-300 rounded-md shadow-sm hover:bg-gray-50 
                focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500
                ${!date && "text-gray-500"} min-w-[280px]
              `}
            >
              <CalendarIcon className="mr-2 h-4 w-4" />
              {formatDateRange(date)}
            </button>
          </PopoverTrigger>
          <PopoverContent className="w-auto p-0" align="start">
            <Calendar
              initialFocus
              mode="range"
              defaultMonth={date?.from}
              selected={date}
              onSelect={handleCalendarSelect}
              numberOfMonths={2}
            />
          </PopoverContent>
        </Popover>
      )}
    </div>
  )
}